import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonVariant } from './Button';
import { ErrorAlert } from './feedback';

// Portals are body siblings. Preserve pre-existing inert/overflow state across the entire stack.
const modalStack: HTMLElement[] = [];
const inertBefore = new Map<HTMLElement, boolean>();
let previousOverflow = '';
let observer: MutationObserver | undefined;
function syncModalStack() {
  const top = modalStack.at(-1);
  for (const child of document.body.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (!inertBefore.has(child)) inertBefore.set(child, child.inert);
    child.inert = top ? child !== top : inertBefore.get(child)!;
  }
  modalStack.forEach((layer, index) => {
    layer.style.zIndex = String(50 + index);
  });
}

/** Accessible dialog: labelled, focus-trapped, Escape to close, focus restored on close. */
export function Modal({
  title,
  onClose,
  children,
  actions,
  wide,
  dismissible = true,
  className = '',
  placement = 'center',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  dismissible?: boolean;
  className?: string;
  placement?: 'center' | 'right';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const backdrop = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  const canDismiss = useRef(dismissible);
  useEffect(() => {
    close.current = onClose;
    canDismiss.current = dismissible;
  }, [onClose, dismissible]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const layer = backdrop.current!;
    if (!modalStack.length) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      observer = new MutationObserver(syncModalStack);
      observer.observe(document.body, { childList: true });
    }
    modalStack.push(layer);
    syncModalStack();
    const el = ref.current;
    const focusables = () =>
      [
        ...(el?.querySelectorAll<HTMLElement>(
          'button, [href], input:not([type="hidden"]), select, textarea, summary, audio[controls], video[controls], [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter(
        (n) =>
          !n.matches(':disabled') &&
          !n.closest('[inert], [hidden], [aria-hidden="true"]') &&
          n.tabIndex >= 0 &&
          (n.checkVisibility?.({ visibilityProperty: true }) ?? true),
      );
    (el?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0] ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (modalStack.at(-1) !== layer) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (canDismiss.current) close.current();
      }
      if (e.key === 'Tab') {
        const f = focusables();
        if (!f.length) {
          e.preventDefault();
          el?.focus();
          return;
        }
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && (document.activeElement === first || !el?.contains(document.activeElement))) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last || !el?.contains(document.activeElement))
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      modalStack.splice(modalStack.indexOf(layer), 1);
      syncModalStack();
      if (!modalStack.length) {
        observer?.disconnect();
        for (const [element, inert] of inertBefore) element.inert = inert;
        inertBefore.clear();
        document.body.style.overflow = previousOverflow;
      }
      if (previous?.isConnected && !previous.closest('[inert]')) previous.focus();
    };
  }, []);
  return createPortal(
    <div
      ref={backdrop}
      className={`modal-backdrop${placement === 'right' ? ' panel-backdrop' : ''}`}
      onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        className={`modal${wide ? ' wide' : ''} ${className}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 className="modal-title" id={titleId}>
          {title}
        </h2>
        {children}
        {actions && <div className="modal-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation for consequential actions; destructive ones are visually distinct. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'primary',
  onConfirm,
  onClose,
  pending,
  error,
  disabled,
  wide,
}: {
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ButtonVariant;
  onConfirm: () => void;
  onClose: () => void;
  pending?: boolean;
  error?: unknown;
  disabled?: boolean;
  wide?: boolean;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      wide={wide}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={onConfirm} loading={pending} disabled={disabled}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {variant === 'danger' && <TriangleAlert size={40} color="var(--color-error)" aria-hidden />}
      {children}
      <ErrorAlert error={error} />
    </Modal>
  );
}
